import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
  buildDefinitionFromStore,
  selectHasConceptGroupErrors,
  selectValidationNonce,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useValidateAVLogicTranslationMutation } from '@verantos-dev/data-access-cohorts';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

interface AVLogicErrorsResult {
  isDrillDownDisabled: boolean;
  drillDownTooltip: string | undefined;
  isValidating: boolean;
}

export const useAVLogicErrors = (analyticalVariable: AnalyticalVariable): AVLogicErrorsResult => {
  const intl = useIntl();
  const { projectId, cohortId } = useParams();

  // --- Client-side: unresolved concept groups (computed by useGetAVCriteriaStatus, stored in Zustand) ---
  const hasConceptGroupErrors = useCohortDefinitionStore(
    selectHasConceptGroupErrors(analyticalVariable.id)
  );

  // --- Validation signal from store (per-AV nonce, bumped by edit actions) ---
  const validationNonce = useCohortDefinitionStore(selectValidationNonce(analyticalVariable.id));

  const [validateTranslation, { isError: hasTranslationError, isLoading: isTranslationLoading }] =
    useValidateAVLogicTranslationMutation();

  // --- Initial validation on mount ---
  const hasRunInitialRef = useRef(false);
  useEffect(() => {
    if (hasRunInitialRef.current) return;
    if (!projectId || !cohortId || !analyticalVariable.name || hasConceptGroupErrors) return;
    hasRunInitialRef.current = true;
    validateTranslation({
      projectId,
      cohortId,
      cohortJsonDsl: buildDefinitionFromStore(),
      startingCohortNode: `ANALYTICAL_VARIABLE::${analyticalVariable.name}`,
    });
  }, [projectId, cohortId, analyticalVariable.name, hasConceptGroupErrors, validateTranslation]);

  // --- Revalidate when an edit action bumps the nonce ---
  useEffect(() => {
    if (validationNonce === 0) return; // No edit yet — initial validation handled above
    if (!projectId || !cohortId || !analyticalVariable.name || hasConceptGroupErrors) return;

    validateTranslation({
      projectId,
      cohortId,
      cohortJsonDsl: buildDefinitionFromStore(),
      startingCohortNode: `ANALYTICAL_VARIABLE::${analyticalVariable.name}`,
    });
  }, [validationNonce]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally minimal: only fires on nonce bumps

  // --- Combine error sources ---
  const isValidating = isTranslationLoading && !hasConceptGroupErrors;
  const isDrillDownDisabled = hasConceptGroupErrors || hasTranslationError || isValidating;

  const drillDownTooltip = (() => {
    if (hasConceptGroupErrors) {
      return intl.formatMessage({
        id: 'projects.analyticalVariables.drillDown.disabled.unresolvedConceptGroups',
      });
    }
    if (isValidating) {
      return intl.formatMessage({
        id: 'projects.analyticalVariables.drillDown.disabled.validating',
      });
    }
    if (hasTranslationError) {
      return intl.formatMessage({
        id: 'projects.analyticalVariables.drillDown.disabled.translationFailed',
      });
    }
    return undefined;
  })();

  return { isDrillDownDisabled, drillDownTooltip, isValidating };
};
